/**
 * What the register's Browse walks: the category tree (decision 214).
 *
 * The client, 2026-10-14: "in register i see browse … i click 1, then i get
 * another list, then i get all — goggles for example, i would think i would
 * get gender after clicking goggles but i just get all skus." Browse walks the
 * store's own tree now — Category, then Subcategory, as deep as it goes — and
 * at any step where the styles below split by gender it offers the gender
 * groups first (Mens, Ladies, Kids), beside whatever has no gender. A long list
 * of styles is narrowed by brand on screen (the dialog's job, not this one's).
 *
 * Seven-ish choices a step (DESIGN-DNA, Hick's law): where the groups are
 * offered, a subcategory reachable through them is not repeated beside them —
 * "All of Clothing" lists every subcategory for anyone who wants it.
 *
 * Pure, so the rules are tested here rather than through a rendered dialog.
 */

export interface BrowseNode {
  readonly id: string;
  readonly name: string;
  readonly parentId: string | null;
  /** "Clothing › Outerwear › Parka". */
  readonly path: string;
}

/** A category's styles carry this gender (null: some have none). */
export interface FiledPair {
  readonly departmentId: string;
  readonly genderId: string | null;
}

export interface BrowseGender {
  readonly id: string;
  readonly name: string;
  readonly group: string | null;
}

/** Where the walk is: the top, a category, optionally narrowed to a group. */
export interface BrowseAt {
  readonly nodeId: string | null;
  readonly group: string | null;
  /** Every subcategory, not the groups first. */
  readonly all: boolean;
}

export type BrowseTile =
  | { readonly kind: 'category'; readonly label: string; readonly at: BrowseAt }
  | { readonly kind: 'group'; readonly label: string; readonly at: BrowseAt }
  | { readonly kind: 'all'; readonly label: string; readonly at: BrowseAt };

export interface BrowseView {
  readonly title: string;
  readonly tiles: readonly BrowseTile[];
  /** At the end of the walk: the styles to list. */
  readonly list: {
    readonly departmentId: string;
    readonly genderIds: readonly string[] | null;
  } | null;
}

export const TOP: BrowseAt = { nodeId: null, group: null, all: false };

export interface BrowseModel {
  readonly view: (at: BrowseAt) => BrowseView;
  /** Every category with styles, for "type to go straight to it". */
  readonly everywhere: readonly { readonly label: string; readonly at: BrowseAt }[];
}

export function browseModel(
  nodes: readonly BrowseNode[],
  filed: readonly FiledPair[],
  genders: readonly BrowseGender[],
  /** The store's order for its groups — Mens, Ladies, Kids. */
  groupOrder: readonly string[] = [],
): BrowseModel {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const children = new Map<string | null, BrowseNode[]>();
  for (const node of nodes) {
    const parent = node.parentId !== null && byId.has(node.parentId) ? node.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), node]);
  }
  /*
   * The top stays in the store's order — Mens, Ladies, Kids, Other — and
   * everything under it reads A to Z (2026-10-20: "i'm under other and having
   * to jump around visually"). The store's order still runs the reports and
   * count sheets; Browse is looked through by name.
   */
  for (const [parent, list] of children) {
    if (parent !== null)
      list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
  const groupOf = new Map(genders.map((gender) => [gender.id, gender.group]));

  // The genders carried below each category (null = a style with none), memoized.
  const own = new Map<string, Set<string | null>>();
  for (const pair of filed) {
    own.set(pair.departmentId, new Set([...(own.get(pair.departmentId) ?? []), pair.genderId]));
  }
  const belowCache = new Map<string, Set<string | null>>();
  const below = (id: string): Set<string | null> => {
    const cached = belowCache.get(id);
    if (cached !== undefined) return cached;
    const out = new Set(own.get(id) ?? []);
    belowCache.set(id, out);
    for (const child of children.get(id) ?? [])
      for (const gender of below(child.id)) out.add(gender);
    return out;
  };
  const hasStyles = (id: string) => below(id).size > 0;
  const inGroup = (id: string, group: string) =>
    [...below(id)].some((gender) => gender !== null && groupOf.get(gender) === group);
  /** Has a style with no gender, or a gender in no group. */
  const loose = (id: string) =>
    [...below(id)].some((gender) => gender === null || (groupOf.get(gender) ?? null) === null);
  const groupsBelow = (id: string): string[] => {
    const found = new Set<string>();
    for (const gender of below(id)) {
      const group = gender === null ? null : (groupOf.get(gender) ?? null);
      if (group !== null) found.add(group);
    }
    // In the store's order, then any group it has not ordered.
    const order = [
      ...new Set([
        ...groupOrder,
        ...genders.flatMap((gender) => (gender.group === null ? [] : [gender.group])),
      ]),
    ];
    return order.filter((group) => found.has(group));
  };
  const gendersIn = (group: string) =>
    genders.filter((gender) => gender.group === group).map((gender) => gender.id);

  const view = (at: BrowseAt): BrowseView => {
    if (at.nodeId === null) {
      return {
        title: 'Browse by category',
        tiles: (children.get(null) ?? [])
          .filter((node) => hasStyles(node.id))
          .map((node) => ({
            kind: 'category' as const,
            label: node.name,
            at: { nodeId: node.id, group: null, all: false },
          })),
        list: null,
      };
    }
    const node = byId.get(at.nodeId)!;
    const title = at.group === null ? node.path : `${node.path} › ${at.group}`;
    const kids = (children.get(node.id) ?? []).filter(
      (child) => hasStyles(child.id) && (at.group === null || inGroup(child.id, at.group)),
    );
    const groups = at.group === null ? groupsBelow(node.id) : [];
    const groupTiles: BrowseTile[] = groups.map((group) => ({
      kind: 'group',
      label: group,
      at: { nodeId: node.id, group, all: false },
    }));
    const kidTile = (child: BrowseNode): BrowseTile => ({
      kind: 'category',
      label: child.name,
      at: { nodeId: child.id, group: at.group, all: false },
    });

    if (kids.length === 0) {
      // The end of the tree: its styles — or, where they split by gender, the groups first.
      if (groups.length >= 2 && !at.all) {
        return {
          title,
          tiles: [
            ...groupTiles,
            { kind: 'all', label: `All of ${node.name}`, at: { ...at, all: true } },
          ],
          list: null,
        };
      }
      return {
        title,
        tiles: [],
        list: { departmentId: node.id, genderIds: at.group === null ? null : gendersIn(at.group) },
      };
    }
    if (groups.length >= 2 && !at.all) {
      const shown = kids.filter((child) => loose(child.id));
      return {
        title,
        tiles: [
          ...groupTiles,
          ...shown.map(kidTile),
          ...(shown.length < kids.length
            ? [{ kind: 'all' as const, label: `All of ${node.name}`, at: { ...at, all: true } }]
            : []),
        ],
        list: null,
      };
    }
    return { title, tiles: kids.map(kidTile), list: null };
  };

  const everywhere = nodes
    .filter((node) => hasStyles(node.id))
    .map((node) => ({ label: node.path, at: { nodeId: node.id, group: null, all: false } }));

  return { view, everywhere };
}

/**
 * A long list narrowed by brand first (2026-10-15: "a brand step where a list
 * is long"): more than a screenful and at least two brands.
 */
export const BRAND_STEP_OVER = 12;

export function brandStep(
  styles: readonly { readonly brand: string | null }[],
): readonly { readonly brand: string; readonly count: number }[] | null {
  if (styles.length <= BRAND_STEP_OVER) return null;
  const counts = new Map<string, number>();
  for (const style of styles) {
    const brand = style.brand ?? 'No brand';
    counts.set(brand, (counts.get(brand) ?? 0) + 1);
  }
  if (counts.size < 2) return null;
  return [...counts]
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => a.brand.localeCompare(b.brand, undefined, { sensitivity: 'base' }));
}
